<template>
  <div v-if="visible" class="cookie-banner">
    <div class="cookie-banner-content">
      <div class="cookie-text">
        <h3>🍪 Gestion des cookies</h3>
        <p>
          Nous utilisons des cookies pour améliorer votre expérience sur OKOLISY.
          <a href="#" @click.prevent="showDetails = !showDetails">En savoir plus</a>
        </p>
        
        <div v-if="showDetails" class="cookie-details">
          <div class="cookie-category">
            <label>
              <input type="checkbox" checked disabled>
              <strong>Cookies nécessaires</strong>
            </label>
            <p>Essentiels au fonctionnement du site.</p>
          </div>
          
          <div class="cookie-category">
            <label>
              <input type="checkbox" v-model="tempPreferences.functional">
              <strong>Cookies fonctionnels</strong>
            </label>
            <p>Mémorisent vos préférences.</p>
          </div>
          
          <div class="cookie-category">
            <label>
              <input type="checkbox" v-model="tempPreferences.analytics">
              <strong>Cookies analytiques</strong>
            </label>
            <p>Nous aident à comprendre votre utilisation.</p>
          </div>
          
          <div class="cookie-category">
            <label>
              <input type="checkbox" v-model="tempPreferences.marketing">
              <strong>Cookies marketing</strong>
            </label>
            <p>Pour des publicités personnalisées.</p>
          </div>
          
          <button class="btn-save" @click="handleSavePreferences">
            Enregistrer mes préférences
          </button>
        </div>
      </div>
      
      <div class="cookie-actions">
        <button v-if="!showDetails" class="btn-customize" @click="showDetails = true">
          Personnaliser
        </button>
        <button class="btn-refuse" @click="handleRefuse">
          Refuser
        </button>
        <button class="btn-accept" @click="handleAcceptAll">
          Tout accepter
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue';
import { useCookies } from '@/composables/useCookies';
import type { CookiePreferences } from '@/services/cookieService';

export default defineComponent({
  name: 'CookieBanner',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:visible', 'accept', 'refuse', 'save'],
  setup(props, { emit }) {
    const { acceptAll, refuseAll, setPreferences } = useCookies();
    const showDetails = ref(false);
    const tempPreferences = ref<CookiePreferences>({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    });

    const handleAcceptAll = (): void => {
      acceptAll();
      emit('accept');
      emit('update:visible', false);
    };

    const handleRefuse = (): void => {
      refuseAll();
      emit('refuse');
      emit('update:visible', false);
    };

    const handleSavePreferences = (): void => {
      setPreferences(tempPreferences.value);
      emit('save', tempPreferences.value);
      emit('update:visible', false);
      showDetails.value = false;
    };

    watch(showDetails, (newVal) => {
      if (newVal) {
        const current = localStorage.getItem('cookie_preferences');
        if (current) {
          try {
            const parsed = JSON.parse(current);
            tempPreferences.value = {
              necessary: true,
              functional: parsed.functional ?? true,
              analytics: parsed.analytics ?? true,
              marketing: parsed.marketing ?? true
            };
          } catch {
            // Ignorer
          }
        }
      }
    });

    return {
      showDetails,
      tempPreferences,
      handleAcceptAll,
      handleRefuse,
      handleSavePreferences
    };
  }
});
</script>

<style lang="scss" scoped>
.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: white;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  padding: 20px;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from { transform: translateY(100%); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  .cookie-banner-content {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media (min-width: 768px) {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  }

  .cookie-text {
    flex: 1;
    
    h3 {
      font-size: 16px;
      margin: 0 0 4px 0;
      color: #333;
    }
    
    p {
      font-size: 14px;
      margin: 0;
      color: #666;
      line-height: 1.5;
      
      a {
        color: #E67E22;
        text-decoration: underline;
        cursor: pointer;
        
        &:hover {
          color: #d35400;
        }
      }
    }
    
    .cookie-details {
      margin-top: 12px;
      padding: 12px;
      background: #f8f9fa;
      border-radius: 8px;
      
      .cookie-category {
        margin-bottom: 10px;
        
        &:last-child {
          margin-bottom: 0;
        }
        
        label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 14px;
          color: #333;
          
          input[type="checkbox"] {
            width: 16px;
            height: 16px;
            cursor: pointer;
            
            &:disabled {
              opacity: 0.6;
              cursor: not-allowed;
            }
          }
        }
        
        p {
          margin: 2px 0 0 28px;
          font-size: 12px;
          color: #999;
        }
      }
      
      .btn-save {
        margin-top: 10px;
        padding: 8px 20px;
        background: #E67E22;
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        
        &:hover {
          background: #d35400;
        }
      }
    }
  }

  .cookie-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
    
    button {
      padding: 10px 20px;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: all 0.2s;
      white-space: nowrap;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
      
      &.btn-accept {
        background: #E67E22;
        color: white;
        &:hover { background: #d35400; }
      }
      
      &.btn-refuse {
        background: #f5f5f5;
        color: #333;
        &:hover { background: #e8e8e8; }
      }
      
      &.btn-customize {
        background: transparent;
        color: #666;
        text-decoration: underline;
        &:hover { color: #333; transform: none; box-shadow: none; }
      }
    }
  }
}

@media (max-width: 768px) {
  .cookie-banner {
    padding: 16px;
    
    .cookie-actions {
      flex-direction: column;
      width: 100%;
      button { width: 100%; text-align: center; }
    }
  }
}
</style>